// ============================================================================
// API — Estado de publicación (Guardar vs Publicar).
//
// El panel distingue DOS acciones con efectos distintos:
//   - Guardar: persiste la configuración en la BD. El storefront NO la ve
//     hasta la siguiente publicación.
//   - Publicar: genera el snapshot versionado que consume la tienda pública
//     (ver supabase/functions/publicar_tienda).
//
// Este módulo responde "¿hay cambios pendientes de publicar?" combinando dos
// señales sin tocar la base de datos (la lógica no vive en SQL):
//   1. Marcador local: cada guardada de `tema_visual`/`editor_tienda`
//      (configuracion_empresa, que no tiene `actualizado_en`) lo enciende;
//      la publicación exitosa lo apaga.
//   2. Recuento en BD: si la última publicación exitosa es MÁS VIEJA que el
//      `actualizado_en` más reciente de las tablas de contenido (empresa,
//      perfil, bloques, banners, categorías, productos, seo), es que algo
//      cambió después de publicar.
// Una tienda jamás publicada siempre se considera con cambios pendientes
// (aún no está en línea).
// ============================================================================

import { supabaseClient } from '@plataforma/supabase-client';

import { obtenerEstadoSesion } from '../estado-sesion.js';

function empresaIdActual() {
  return obtenerEstadoSesion().claims.empresa_id;
}

const PREFIJO_CLAVE = 'pf:cambios-pendientes:';

function claveLocal() {
  return `${PREFIJO_CLAVE}${empresaIdActual()}`;
}

export function marcarCambiosPendientes() {
  localStorage.setItem(claveLocal(), '1');
}

export function limpiarCambiosPendientes() {
  localStorage.removeItem(claveLocal());
}

const TABLAS_CONTENIDO = [
  'empresa',
  'empresa_perfil',
  'bloque_pagina',
  'banner',
  'categoria',
  'producto',
  'seo',
];

/** Devuelve el instante (ms) del cambio más reciente en las tablas de contenido. */
async function ultimoCambioContenido(empresaId) {
  const consultas = TABLAS_CONTENIDO.map((tabla) => {
    let consulta = supabaseClient().from(tabla).select('actualizado_en');
    consulta =
      tabla === 'empresa' ? consulta.eq('id', empresaId) : consulta.eq('empresa_id', empresaId);
    return consulta.order('actualizado_en', { ascending: false }).limit(1).maybeSingle();
  });

  const resultados = await Promise.all(consultas);
  const instantes = resultados
    .filter(({ data }) => data?.actualizado_en)
    .map(({ data }) => new Date(data.actualizado_en).getTime());

  return instantes.length ? Math.max(...instantes) : null;
}

/**
 * Estado de publicación actual de la tienda.
 * @returns {Promise<{ publicada: boolean, pendiente: boolean, ultimaPublicacion: string | null, error: Error | null }>}
 */
export async function obtenerEstadoPublicacion() {
  const empresaId = empresaIdActual();

  const [publicacionRes, maxContenido] = await Promise.all([
    supabaseClient()
      .from('publicacion')
      .select('finalizado_en')
      .eq('empresa_id', empresaId)
      .eq('estado', 'exitosa')
      .order('finalizado_en', { ascending: false })
      .limit(1)
      .maybeSingle(),
    ultimoCambioContenido(empresaId),
  ]);

  if (publicacionRes.error) {
    return {
      publicada: false,
      pendiente: true,
      ultimaPublicacion: null,
      error: publicacionRes.error,
    };
  }

  const ultimaPublicacion = publicacionRes.data?.finalizado_en ?? null;
  const publicada = Boolean(ultimaPublicacion);
  const pendienteLocal = localStorage.getItem(claveLocal()) === '1';
  const pendiente =
    !publicada ||
    pendienteLocal ||
    (maxContenido !== null &&
      ultimaPublicacion !== null &&
      maxContenido > new Date(ultimaPublicacion).getTime());

  return { publicada, pendiente, ultimaPublicacion, error: null };
}
